import { Link } from "react-router-dom";
import { Mail, Phone, Facebook, Instagram, Twitter, Linkedin, LogIn } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const Footer = () => {
  const { toast } = useToast();

  const copyEmail = () => {
    navigator.clipboard.writeText('evan@vincentgrowthpartners.co.uk');
    toast({
      title: "Email copied!",
      description: "evan@vincentgrowthpartners.co.uk has been copied to your clipboard.",
    });
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText('+447857450763');
    toast({
      title: "Phone number copied!",
      description: "+447857450763 is in your clipboard. Shall we start your growth journey?",
      duration: 5000,
    });
  };

  return (
    <footer className="bg-[#0f1a2a] text-cream py-12 border-t border-gold/10">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="md:col-span-1">
            <h3 className="text-2xl font-bold mb-2 italic text-[#B88C3C]">Vincent Growth Partners</h3>
            <p className="text-white/60 text-sm mb-4">See what we're up to & what clients say</p>
            {/* 2x3 Social Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div 
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors cursor-pointer"
                onClick={copyEmail}
                title="Copy email address"
              >
                <Mail className="w-5 h-5 text-[#B88C3C]" />
              </div>
              <a 
                href="https://www.linkedin.com/company/vincent-growth-partners/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
                title="Connect with us on LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-[#B88C3C]" />
              </a>
              <a 
                href="https://www.instagram.com/vincentgrowthpartners/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
                title="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5 text-[#B88C3C]" />
              </a>
              <a 
                href="https://x.com/GrowthVgp"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
                title="Follow us on X"
              >
                <Twitter className="w-5 h-5 text-[#B88C3C]" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61578173273716"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
                title="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5 text-[#B88C3C]" />
              </a>
              <a 
                href="https://uk.trustpilot.com/review/vincentgrowthpartners.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#00b67a]/20 rounded-full flex items-center justify-center hover:bg-[#00b67a]/30 transition-colors"
                title="Read our Trustpilot reviews"
              >
                <svg className="w-5 h-5 text-[#00b67a]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0L15.09 7.26L23 8.27L17.5 13.14L18.82 21.02L12 17.27L5.18 21.02L6.5 13.14L1 8.27L8.91 7.26L12 0Z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-1">
            <h4 className="text-lg font-semibold mb-4 text-white">Get In Touch</h4>
            <div className="space-y-3 text-white/80">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#B88C3C] flex-shrink-0" />
                <div>
                  <div 
                    onClick={copyPhoneNumber}
                    className="font-semibold hover:text-[#B88C3C] transition-colors cursor-pointer text-sm"
                    title="Click to copy phone number"
                  >
                    07857 450763
                  </div>
                  <p className="text-xs text-white/50">AI Reception - 24/7 Available</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[#B88C3C] flex-shrink-0" />
                <span 
                  className="cursor-pointer hover:text-[#B88C3C] transition-colors text-sm break-all"
                  onClick={copyEmail}
                  title="Click to copy email address"
                >
                  evan@vincentgrowthpartners.co.uk
                </span>
              </div>
              <Link
                to="/auth"
                className="flex items-center space-x-3 group"
              >
                <LogIn className="w-5 h-5 text-[#B88C3C] flex-shrink-0" />
                <span className="text-sm font-semibold group-hover:text-[#B88C3C] transition-colors">
                  Client Portal Login
                </span>
              </Link>
            </div>
          </div>

          {/* Grow With Us - LinkedIn Newsletter */}
          <div className="md:col-span-1">
            <h4 className="text-lg font-semibold mb-4 text-white">Grow With Us</h4>
            <div className="bg-white/5 border border-[#B88C3C]/20 rounded-lg p-4">
              <p className="text-[#B88C3C] font-semibold text-sm mb-2">📰 Weekly Growth Tips</p>
              <p className="text-white/60 text-xs mb-3">Actionable marketing strategies delivered to your feed</p>
              <a 
                href="https://www.linkedin.com/build-relation/newsletter-follow?entityUrn=7376990429063577600"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-4 py-2 rounded-lg text-white bg-[#0A66C2] hover:bg-[#094c93] transition-colors text-sm font-medium"
              >
                Subscribe on LinkedIn
              </a>
            </div>
          </div>
        </div>

        {/* SEO topic cluster internal links - boosts ranking for B2B lead generation */}
        <div className="mt-10 pt-8 border-t border-white/10">
          <h4 className="text-sm font-semibold text-white/70 mb-3 text-center">B2B Lead Generation Resources</h4>
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-white/60">
            <Link to="/b2b-lead-generation" className="hover:text-[#B88C3C] transition-colors">B2B Lead Generation Agency UK</Link>
            <Link to="/b2b-lead-generation/london" className="hover:text-[#B88C3C] transition-colors">B2B Lead Generation London</Link>
            <Link to="/b2b-lead-generation/manchester" className="hover:text-[#B88C3C] transition-colors">Manchester</Link>
            <Link to="/b2b-lead-generation/birmingham" className="hover:text-[#B88C3C] transition-colors">Birmingham</Link>
            <Link to="/b2b-lead-generation/leeds" className="hover:text-[#B88C3C] transition-colors">Leeds</Link>
            <Link to="/b2b-lead-generation/bristol" className="hover:text-[#B88C3C] transition-colors">Bristol</Link>
            <Link to="/b2b-lead-generation/edinburgh" className="hover:text-[#B88C3C] transition-colors">Edinburgh</Link>
            <Link to="/b2b-lead-generation/uk-cities" className="hover:text-[#B88C3C] transition-colors">All UK Cities</Link>
            <Link to="/linkedin-lead-generation" className="hover:text-[#B88C3C] transition-colors">LinkedIn Lead Generation</Link>
            <Link to="/b2b-lead-generation-cost" className="hover:text-[#B88C3C] transition-colors">B2B Lead Generation Cost</Link>
            <Link to="/b2b-lead-generation-vs-in-house" className="hover:text-[#B88C3C] transition-colors">Agency vs In-House SDR</Link>
            <Link to="/linkedin-sdr" className="hover:text-[#B88C3C] transition-colors">LinkedIn SDR Service</Link>
            <Link to="/paid-ads" className="hover:text-[#B88C3C] transition-colors">Paid Ads Management</Link>
            <Link to="/website-design" className="hover:text-[#B88C3C] transition-colors">B2B Website Design</Link>
          </nav>
        </div>

        {/* Trustpilot Review Collector */}
        <div className="mt-10 flex justify-center">
          <div
            className="trustpilot-widget"
            data-locale="en-GB"
            data-template-id="56278e9abfbbba0bdcd568bc"
            data-businessunit-id="68b7057cbc94b86a86ac674c"
            data-style-height="52px"
            data-style-width="100%"
            data-token="d201c7ca-93f1-4044-9c10-9fa4a08cec3b"
          >
            <a
              href="https://uk.trustpilot.com/review/vincentgrowthpartners.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00b67a] hover:underline text-sm"
            >
              Leave us a review on Trustpilot
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8 text-center text-white/50 space-y-2">
          <p className="text-xs">Vincent Growth Partners Ltd | Company No. 16166010 | Registered in England & Wales</p>
          <p className="text-xs">&copy; 2025 Vincent Growth Partners Ltd. All rights reserved. | <a href="/privacy-policy" className="hover:text-[#B88C3C] transition-colors">Privacy Policy</a> | <a href="/content-policy" className="hover:text-[#B88C3C] transition-colors">Content Policy</a> | <a href="/terms-of-service" className="hover:text-[#B88C3C] transition-colors">Terms of Service</a></p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;